import React, { useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Sparkles, FileText, Download } from "lucide-react";
import { toast } from "sonner";

export default function WeeklyReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/weekly-report");
      setReport(data);
    } catch { toast.error("Erreur IA"); }
    finally { setLoading(false); }
  };

  return (
    <AppShell>
      <div className="px-5 pt-4">
        <h1 className="font-display text-2xl font-bold text-[var(--text)]" data-testid="report-title">Rapport hebdomadaire</h1>
        <p className="text-sm text-zinc-500">Votre semaine, en un coup d'œil.</p>
      </div>

      <div className="px-5 mt-4">
        <Button onClick={generate} disabled={loading} className="w-full rounded-full bg-emerald-500/100 hover:bg-emerald-500 h-11 font-semibold" data-testid="generate-report">
          <Sparkles className="w-4 h-4 mr-2" />{loading ? "Prosperr rédige…" : "Générer le rapport"}
        </Button>
      </div>

      {report && (
        <div className="px-5 mt-5 space-y-4" data-testid="report-output">
          <div className="bg-[var(--surface)] rounded-xl p-5 border border-[var(--border-soft)] shadow-sm">
            <div className="font-display font-bold text-[var(--text)] flex items-center gap-2"><FileText className="w-5 h-5 text-emerald-400" />Résumé</div>
            <p className="mt-2 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{report.narrative}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              ["prospects", "Prospects"],
              ["interactions", "Actions"],
              ["responses", "Réponses"],
              ["rdv", "RDV"],
              ["converted", "Conversions"],
            ].map(([k, l]) => (
              <div key={k} className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
                <div className="text-xs text-zinc-500 font-semibold">{l}</div>
                <div className="font-display text-3xl font-bold text-[var(--text)]">{report.metrics?.[k] ?? 0}</div>
              </div>
            ))}
          </div>

          {report.pipeline_health !== undefined && (
            <div className="bg-[var(--surface)] rounded-xl p-5 border border-[var(--border-soft)] shadow-sm">
              <div className="font-display font-bold text-[var(--text)]">Santé du tunnel</div>
              <div className="font-display text-4xl font-bold text-emerald-400 mt-2">{report.pipeline_health}<span className="text-base text-zinc-500">/100</span></div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden mt-3">
                <div className="h-full bg-emerald-500/100" style={{ width: `${report.pipeline_health}%` }} />
              </div>
              {report.explanation && <p className="text-xs text-zinc-500 mt-2">{report.explanation}</p>}
            </div>
          )}

          {report.top_actions?.length > 0 && (
            <div className="bg-[var(--surface)] rounded-xl p-5 border border-[var(--border-soft)] shadow-sm">
              <div className="font-display font-bold text-[var(--text)]">🏆 Top actions</div>
              <ul className="mt-2 space-y-1 text-sm text-zinc-300 list-disc ml-4">
                {report.top_actions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}

          {report.recommendations?.length > 0 && (
            <div className="bg-emerald-500/10 rounded-2xl p-5 border border-emerald-500/30">
              <div className="font-display font-bold text-[var(--text)]">💡 Recommandations</div>
              <ul className="mt-2 space-y-1 text-sm text-zinc-300 list-disc ml-4">
                {report.recommendations.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
